<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="560px"
    top="5vh"
    @close="handleClose"
    class="leaderboard-modal"
  >
    <div class="leaderboard-container">
      <!-- Header -->
      <div class="leaderboard-header">
        <h2 class="campaign-title">{{ campaignTitle }}</h2>
        
        <!-- Duration Progress Bar -->
        <div class="progress-section">
          <div class="progress-bar-container">
            <div class="progress-bar-fill" :style="{ width: durationProgress + '%' }"></div>
          </div>
          <div class="progress-label">{{ durationLabel }}</div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <loading-even></loading-even>
      </div>

      <!-- Upcoming Campaign - Show Sign-ups -->
      <div v-else-if="isUpcoming" class="signups-section">
        <!-- Sign-ups Empty State -->
        <div v-if="!signups || signups.length === 0" class="empty-state">
          <div class="empty-icon">📋</div>
          <div class="empty-text">{{ $t("happilyVillage.no_signups_yet") }}</div>
          <div class="empty-subtext">{{ $t("happilyVillage.signups_empty_hint") }}</div>
        </div>

        <!-- Sign-ups Header (fixed) -->
        <div v-else class="signups-wrapper">
          <div class="signups-header">
            <span class="signups-count">{{ signups.length }} {{ $t("happilyVillage.signed_up_count") }}</span>
          </div>
          <!-- Sign-ups List (scrollable) -->
          <div class="signups-list thin-scrollbar">
            <div
              v-for="(user, index) in signups"
              :key="user.email || index"
              class="signup-row"
            >
            <!-- Avatar -->
            <div class="avatar-column">
              <div class="avatar-wrapper">
                <img
                  v-if="user.photo_url"
                  :src="user.photo_url"
                  :alt="user.name"
                  class="participant-avatar"
                />
                <div v-else class="participant-avatar-placeholder">
                  {{ getInitials(user.name) }}
                </div>
              </div>
            </div>

            <!-- Name & Team -->
            <div class="name-column">
              <div class="participant-name">{{ user.name || user.email }}</div>
              <div class="participant-team" v-if="user.team_name">{{ user.team_name }}</div>
            </div>

            <!-- Joined indicator -->
            <div class="joined-column">
              <span class="joined-badge">✓ {{ $t("happilyVillage.signed_up_badge") }}</span>
            </div>
          </div>
          </div>
        </div>
      </div>

      <!-- Empty State (for active/ended campaigns with no participants) -->
      <div v-else-if="!leaderboard || leaderboard.length === 0" class="empty-state">
        <div class="empty-icon">🏆</div>
        <div class="empty-text">{{ $t("happilyVillage.no_participants_yet") }}</div>
        <div class="empty-subtext">{{ $t("happilyVillage.participants_empty_hint") }}</div>
      </div>

      <!-- Leaderboard List (for active/ended campaigns) -->
      <div v-else class="leaderboard-list thin-scrollbar">
        <div
          v-for="(participant, index) in leaderboard"
          :key="participant.email || index"
          class="participant-row"
          :class="{ 'top-three': participant.rank <= 3 }"
        >
          <!-- Rank -->
          <div class="rank-column">
            <span v-if="participant.rank === 1" class="rank-medal">🥇</span>
            <span v-else-if="participant.rank === 2" class="rank-medal">🥈</span>
            <span v-else-if="participant.rank === 3" class="rank-medal">🥉</span>
            <span v-else class="rank-number">{{ participant.rank }}</span>
          </div>

          <!-- Avatar with achievement indicator -->
          <div class="avatar-column">
            <div class="avatar-wrapper" :class="{ 'achieved': hasAchievedGoal(participant) }">
              <img
                v-if="participant.photo_url"
                :src="participant.photo_url"
                :alt="participant.name"
                class="participant-avatar"
              />
              <div v-else class="participant-avatar-placeholder">
                {{ getInitials(participant.name) }}
              </div>
              <span v-if="hasAchievedGoal(participant)" class="check-badge">✓</span>
            </div>
          </div>

          <!-- Name & Team -->
          <div class="name-column">
            <div class="participant-name">{{ participant.name }}</div>
            <div class="participant-team" v-if="participant.team_name">{{ participant.team_name }}</div>
          </div>

          <!-- Score -->
          <div class="stats-column">
            <div class="score-row">
              <span class="score-value" :class="{ 'achieved': hasAchievedGoal(participant) }">{{ formatValue(participant.value) }}</span>
              <span class="score-unit" v-if="unit">{{ unit }}</span>
            </div>
            <div class="coins-earned" v-if="getCoinsEarned(participant) > 0">
              +{{ formatValue(getCoinsEarned(participant)) }} {{ $t("happilyVillage.coins") }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Summary Footer -->
      <div class="summary-footer" v-if="participantCount > 0">
        <span class="footer-stat">{{ achieversCount }}/{{ participantCount }} {{ $t("happilyVillage.achieved_goal") }}</span>
        <span class="footer-divider" v-if="totalCoinsAwarded > 0">·</span>
        <span class="footer-stat" v-if="totalCoinsAwarded > 0">{{ formatValue(totalCoinsAwarded) }} {{ $t("happilyVillage.coins_awarded") }}</span>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "CampaignLeaderboardModal",
  components: {
    loadingEven: require("../even/loading").default,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    campaign: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      signups: [],
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        if (!val) {
          this.$emit("close");
        }
      },
    },
    campaignTitle() {
      if (!this.campaign) return "";
      const title = this.campaign.title;
      if (!title) return "";
      if (typeof title === "string") return title;
      if (typeof title === "object") {
        return title.en || title.th || Object.values(title).find(v => v) || "";
      }
      return "";
    },
    unit() {
      if (!this.campaign || !this.campaign.goals) return "";
      const unit = this.campaign.goals.unit;
      if (!unit) return "";
      if (typeof unit === "string") return unit;
      if (typeof unit === "object") {
        return unit.en || unit.th || "";
      }
      return "";
    },
    summary() {
      if (!this.campaign || !this.campaign.id) return null;
      return this.$store.getters["happilyvillageUniversity/getCampaignSummary"](this.campaign.id);
    },
    leaderboard() {
      if (!this.summary || !this.summary.leaderboard) return [];
      // Sort by rank
      return [...this.summary.leaderboard].sort((a, b) => (a.rank || 999) - (b.rank || 999));
    },
    participantCount() {
      return this.leaderboard.length;
    },
    individualTarget() {
      if (!this.campaign || !this.campaign.goals) return null;
      return this.campaign.goals.individualTarget || null;
    },
    rewardConfig() {
      if (!this.campaign || !this.campaign.reward) return null;
      return this.campaign.reward;
    },
    achieversCount() {
      if (!this.individualTarget) return 0;
      return this.leaderboard.filter(p => this.hasAchievedGoal(p)).length;
    },
    totalCoinsAwarded() {
      return this.leaderboard.reduce((total, p) => total + this.getCoinsEarned(p), 0);
    },
    durationProgress() {
      // Calculate progress based on campaign duration
      if (!this.campaign || !this.campaign.timing) return 0;
      
      const timing = this.campaign.timing;
      if (!timing.startDate || !timing.endDate) return 0;
      
      const startDate = this.parseDate(timing.startDate);
      const endDate = this.parseDate(timing.endDate);
      const now = new Date();
      
      if (!startDate || !endDate) return 0;
      
      // If campaign hasn't started yet
      if (now < startDate) return 0;
      
      // If campaign is completed
      if (now >= endDate) return 100;
      
      // Calculate percentage
      const totalDuration = endDate.getTime() - startDate.getTime();
      const elapsed = now.getTime() - startDate.getTime();
      
      return Math.round((elapsed / totalDuration) * 100);
    },
    durationLabel() {
      if (!this.campaign || !this.campaign.timing) return '';
      
      const timing = this.campaign.timing;
      if (!timing.startDate || !timing.endDate) return '';
      
      const startDate = this.parseDate(timing.startDate);
      const endDate = this.parseDate(timing.endDate);
      const now = new Date();
      
      if (!startDate || !endDate) return '';
      
      // If campaign hasn't started yet
      if (now < startDate) {
        const diffTime = startDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays <= 1) return this.$t("happilyVillage.starts_tomorrow");
        return `${this.$t("happilyVillage.starts_in")} ${diffDays} ${this.$t("happilyVillage.days")}`;
      }

      // If campaign is completed
      if (now >= endDate) return this.$t("happilyVillage.Completed");

      // Calculate days remaining
      const diffTime = endDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 1) return this.$t("happilyVillage.last_day");
      return `${diffDays} ${this.$t("happilyVillage.days_left")}`;
    },
    isUpcoming() {
      if (!this.campaign || !this.campaign.timing) return false;
      const timing = this.campaign.timing;
      if (!timing.startDate) return false;
      
      const startDate = this.parseDate(timing.startDate);
      if (!startDate) return false;
      
      return new Date() < startDate;
    },
  },
  watch: {
    visible(val) {
      if (val && this.campaign && this.campaign.id) {
        this.fetchSummary();
      }
    },
  },
  methods: {
    async fetchSummary() {
      if (!this.campaign || !this.campaign.id) return;
      
      this.loading = true;
      try {
        // For upcoming campaigns, fetch sign-ups instead of leaderboard
        if (this.isUpcoming) {
          this.signups = await this.$store.dispatch("happilyvillageUniversity/fetchCampaignSignups", this.campaign.id);
        } else {
          // For active/ended campaigns, fetch the summary/leaderboard
          const cached = this.$store.getters["happilyvillageUniversity/getCampaignSummary"](this.campaign.id);
          if (!cached) {
            await this.$store.dispatch("happilyvillageUniversity/fetchCampaignSummary", this.campaign.id);
          }
        }
      } finally {
        this.loading = false;
      }
    },
    handleClose() {
      this.signups = [];
      this.$emit("close");
    },
    getInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    formatValue(value) {
      if (value === undefined || value === null) return "0";
      if (typeof value === "number") {
        return value.toLocaleString();
      }
      return value;
    },
    hasAchievedGoal(participant) {
      if (!this.individualTarget || !participant) return false;
      const value = participant.value || 0;
      return value >= this.individualTarget;
    },
    parseDate(dateValue) {
      if (!dateValue) return null;
      // Handle Firestore Timestamp
      if (dateValue.toDate) return dateValue.toDate();
      // Handle Date object
      if (dateValue instanceof Date) return dateValue;
      // Handle string
      if (typeof dateValue === 'string') return new Date(dateValue);
      return null;
    },
    getCoinsEarned(participant) {
      if (!participant || !this.rewardConfig) return 0;
      
      // If participant has coins_earned field from backend, use that
      if (participant.coins_earned !== undefined) {
        return participant.coins_earned;
      }
      
      // Otherwise calculate based on reward type and goal achievement
      const reward = this.rewardConfig;
      const value = participant.value || 0;
      
      if (reward.type === 'instant') {
        // Instant coins: awarded per action/daily goal achieved
        // This would typically be tracked by the backend
        return participant.total_coins || 0;
      } else if (reward.type === 'charged') {
        // Charged coins: accumulate per action, paid at end
        // Check if goal was achieved
        if (this.hasAchievedGoal(participant)) {
          return participant.charged_coins || participant.total_coins || 0;
        }
        return 0;
      } else if (reward.type === 'prize_pool') {
        // Prize pool: split among achievers at end
        if (this.hasAchievedGoal(participant)) {
          return participant.prize_share || participant.total_coins || 0;
        }
        return 0;
      }
      
      return participant.total_coins || 0;
    },
  },
};
</script>

<style scoped>
.leaderboard-modal >>> .el-dialog__header {
  display: none;
}

.leaderboard-modal >>> .el-dialog__body {
  padding: 0;
}

.leaderboard-modal >>> .el-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.leaderboard-container {
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  font-family: 'Lato', 'Kanit', Arial, sans-serif;
}

/* Header */
.leaderboard-header {
  background: #6C30CC;
  padding: 20px 24px;
  color: white;
}

.campaign-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 16px 0;
  line-height: 1.3;
  color: white;
}

/* Progress Section */
.progress-section {
  margin-top: 16px;
}

.progress-bar-container {
  height: 4px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: white;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-label {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

/* Loading State */
.loading-state {
  padding: 60px 24px;
  display: flex;
  justify-content: center;
}

/* Empty State */
.empty-state {
  padding: 60px 24px;
  text-align: center;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
}

.empty-text {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.empty-subtext {
  font-size: 13px;
  color: #9CA3AF;
}

/* Sign-ups Section (Upcoming Campaigns) */
.signups-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.signups-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.signups-list {
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
  max-height: calc(100vh - 280px);
}

.signups-header {
  padding: 12px 20px;
  background: #F0FDF4;
  border-bottom: 1px solid #BBF7D0;
}

.signups-count {
  font-size: 14px;
  font-weight: 600;
  color: #166534;
}

.signup-row {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid #F3F4F6;
}

.signup-row:last-child {
  border-bottom: none;
}

.signup-row:hover {
  background-color: #FAFBFC;
}

.joined-column {
  text-align: right;
}

.joined-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #DCFCE7;
  color: #166534;
  font-size: 12px;
  font-weight: 500;
  border-radius: 12px;
}

/* Leaderboard List */
.leaderboard-list {
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
}

.participant-row {
  display: grid;
  grid-template-columns: 44px 44px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  border-bottom: 1px solid #F3F4F6;
}

.participant-row:last-child {
  border-bottom: none;
}

.participant-row:hover {
  background-color: #FAFBFC;
}

.participant-row.top-three {
  background-color: #FFFBEB;
}

.participant-row.top-three:hover {
  background-color: #FEF3C7;
}

/* Rank Column */
.rank-column {
  text-align: center;
}

.rank-medal {
  font-size: 20px;
}

.rank-number {
  font-size: 14px;
  font-weight: 600;
  color: #9CA3AF;
}

/* Avatar Column */
.avatar-column {
  display: flex;
  justify-content: center;
}

.avatar-wrapper {
  position: relative;
  width: 36px;
  height: 36px;
}

.avatar-wrapper.achieved {
  /* Subtle green ring for achievers */
}

.avatar-wrapper.achieved .participant-avatar,
.avatar-wrapper.achieved .participant-avatar-placeholder {
  box-shadow: 0 0 0 2px #10B981;
}

.participant-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.participant-avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #6B7280;
}

.check-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 14px;
  height: 14px;
  background: #10B981;
  color: white;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
}

/* Name Column */
.name-column {
  min-width: 0;
}

.participant-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.participant-team {
  font-size: 12px;
  color: #9CA3AF;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Stats Column */
.stats-column {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  white-space: nowrap;
}

.score-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.score-value {
  font-size: 15px;
  font-weight: 700;
  color: #6C30CC;
}

.score-value.achieved {
  color: #059669;
}

.score-unit {
  font-size: 12px;
  color: #9CA3AF;
}

.coins-earned {
  font-size: 11px;
  color: #B45309;
  font-weight: 500;
}

/* Summary Footer */
.summary-footer {
  padding: 12px 20px;
  background: #FAFBFC;
  border-top: 1px solid #E5E7EB;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.footer-stat {
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
}

.footer-divider {
  color: #D1D5DB;
}

/* Scrollbar */
.thin-scrollbar::-webkit-scrollbar {
  width: 5px;
}

.thin-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.thin-scrollbar::-webkit-scrollbar-thumb {
  background: #E5E7EB;
  border-radius: 3px;
}

.thin-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #D1D5DB;
}
</style>

